import { Check, ChevronDown, ChevronRight, Tag } from "lucide-react";
import { useEffect, useState } from "react";
import { trpc } from "~/api/trpc";
import { Input } from "~/components/ui/input";
import { type CartItem, type MatchedDiscountCode } from "~/stores/booking-flow-store";
import { Language, useLanguageStore } from "~/stores/language-store";

type UnmatchedReason = {
    reason: 'NOT_FOUND' | 'DATE_INVALID' | 'CUSTOMER_TYPE_NOT_ELIGIBLE' | 'TIME_WINDOW_INVALID' | 'STORE_NOT_APPLICABLE';
    endDate?: string;
    applicationCustomerType?: string;
};

const getDiscountCodeErrorMessage = ({
    matchedDiscountCode,
    unmatchedReason,
    applicableCount,
    isEntirePurchase,
    language,
}: {
    matchedDiscountCode: MatchedDiscountCode | null;
    unmatchedReason: UnmatchedReason | null;
    applicableCount: number;
    isEntirePurchase: boolean;
    language: Language;
}): string | null => {
    if (unmatchedReason) {
        if (unmatchedReason.reason === 'NOT_FOUND') {
            return language === Language.GERMAN
                ? 'Code ungültig (wird vor Ort geprüft)'
                : 'Code invalid (will be checked on-site)';
        }
        if (unmatchedReason.reason === 'DATE_INVALID' && unmatchedReason.endDate) {
            const formattedDate = new Date(unmatchedReason.endDate).toLocaleDateString(
                language === Language.GERMAN ? 'de-DE' : 'en-US',
                { day: '2-digit', month: '2-digit', year: 'numeric' },
            );
            return language === Language.GERMAN
                ? `Nur gültig für Termine bis zum ${formattedDate}`
                : `Only valid for appointments until ${formattedDate}`;
        }
        if (unmatchedReason.reason === 'CUSTOMER_TYPE_NOT_ELIGIBLE') {
            if (unmatchedReason.applicationCustomerType === 'NEW_CUSTOMERS_ONLY') {
                return language === Language.GERMAN
                    ? 'Nur gültig für Neukunden'
                    : 'Only valid for new customers';
            }
            return language === Language.GERMAN
                ? 'Code ungültig (wird vor Ort geprüft)'
                : 'Code invalid (will be checked on-site)';
        }
        if (unmatchedReason.reason === 'TIME_WINDOW_INVALID') {
            return language === Language.GERMAN
                ? 'Dieser Rabattcode ist nur an bestimmten Tagen/Uhrzeiten gültig'
                : 'This discount code is only valid on specific days/times';
        }
        if (unmatchedReason.reason === 'STORE_NOT_APPLICABLE') {
            return language === Language.GERMAN
                ? 'Dieser Rabattcode ist an diesem Standort nicht gültig'
                : 'This discount code is not valid at this location';
        }
    }

    if (matchedDiscountCode && !isEntirePurchase && applicableCount === 0) {
        return language === Language.GERMAN
            ? 'Nicht gültig für die ausgewählten Leistungen'
            : 'Not valid for selected treatments';
    }

    return null;
};

export const getApplicableCartItemCount = (cartItems: CartItem[], matchedDiscountCode: MatchedDiscountCode | null) => matchedDiscountCode
    ? cartItems.filter((item) =>
        !item.isBundleRedemption &&
        (matchedDiscountCode.applicableBookableItemIds.includes(item.bookableItemId) ||
        (item.bundleId && matchedDiscountCode.applicableBundleIds.includes(item.bundleId)))
    ).length
    : 0;

export const isDiscountCodeApplicable = (cartItems: CartItem[], matchedDiscountCode: MatchedDiscountCode | null) =>
    !!matchedDiscountCode
    && (matchedDiscountCode.applicationLevel === 'ENTIRE_PURCHASE' || getApplicableCartItemCount(cartItems, matchedDiscountCode) > 0);

type DiscountCodeSectionProps = {
    discountCode: string;
    onDiscountCodeChange: (code: string) => void;
    matchedDiscountCode: MatchedDiscountCode | null;
    onMatchedDiscountCodeChange: (matched: MatchedDiscountCode | null) => void;
    cartItems: CartItem[];
    appointmentDate?: Date;
    customerEmail?: string;
    storeId?: number;
    readOnly?: boolean;
};

const DiscountCodeSection = ({
    discountCode,
    onDiscountCodeChange,
    matchedDiscountCode,
    onMatchedDiscountCodeChange,
    cartItems,
    appointmentDate,
    customerEmail,
    storeId,
    readOnly = false,
}: DiscountCodeSectionProps) => {
    const { language } = useLanguageStore();

    // CMS Config
    const { data: cmsConfigData } = trpc.publicBookingFunnel.getBookingReviewConfig.useQuery(undefined, {
        staleTime: 60000,
    });
    const cmsConfig = cmsConfigData?.config;

    const [inputValue, setInputValue] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [isApplied, setIsApplied] = useState(!!discountCode);
    const [isValidating, setIsValidating] = useState(false);
    const [unmatchedReason, setUnmatchedReason] = useState<UnmatchedReason | null>(null);

    const utils = trpc.useUtils();

    const validateAndApplyCode = async (code: string) => {
        if (!appointmentDate) return;
        setIsValidating(true);
        try {
            const bookableItemIds = cartItems
                .filter((item) => !item.isBundleRedemption)
                .map((item) => item.bookableItemId);
            const bundleIds = cartItems
                .filter((item) => item.bundleId && !item.isBundleRedemption)
                .map((item) => item.bundleId!);
            const result = await utils.publicBookingFunnel.validateDiscountCode.fetch({
                code,
                bookableItemIds,
                bundleIds,
                appointmentDate: appointmentDate.toISOString(),
                customerEmail,
                storeId,
            });

            if (result.matched) {
                onMatchedDiscountCodeChange({
                    id: result.discountCode.id,
                    name: result.discountCode.name,
                    type: result.discountCode.type as 'ABSOLUTE_DISCOUNT' | 'PERCENTAGE_DISCOUNT',
                    discountValue: result.discountCode.discountValue,
                    applicationLevel: result.discountCode.applicationLevel as 'PER_LINE_ITEM' | 'ENTIRE_PURCHASE',
                    applicableBookableItemIds: result.discountCode.applicableBookableItemIds,
                    applicableBundleIds: result.discountCode.applicableBundleIds,
                });
                setUnmatchedReason(null);
            } else {
                onMatchedDiscountCodeChange(null);
                setUnmatchedReason({
                    reason: result.reason,
                    endDate: 'endDate' in result ? result.endDate : undefined,
                    applicationCustomerType: 'applicationCustomerType' in result ? result.applicationCustomerType : undefined,
                });
            }
        } catch {
            onMatchedDiscountCodeChange(null);
            setUnmatchedReason({ reason: 'NOT_FOUND' });
        } finally {
            setIsValidating(false);
        }
    };

    useEffect(() => {
        if (discountCode) {
            setInputValue(discountCode);
            setIsApplied(true);
            validateAndApplyCode(discountCode);
        }
    }, []);

    const handleApply = async () => {
        const trimmedCode = inputValue.trim();
        if (!trimmedCode) return;
        onDiscountCodeChange(trimmedCode);
        setIsApplied(true);
        await validateAndApplyCode(trimmedCode);
    };

    const handleRemove = () => {
        onDiscountCodeChange('');
        onMatchedDiscountCodeChange(null);
        setUnmatchedReason(null);
        setInputValue('');
        setIsApplied(false);
        setIsOpen(false);
    };

    const formatDiscountLabel = () => {
        if (!matchedDiscountCode) {
            return null;
        }
        if (matchedDiscountCode.type === 'PERCENTAGE_DISCOUNT') {
            return `${matchedDiscountCode.discountValue}% ${language === Language.GERMAN ? 'Rabatt' : 'discount'}`;
        }
        return `${new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(matchedDiscountCode.discountValue)} EUR ${language === Language.GERMAN ? 'Rabatt' : 'discount'}`;
    };

    const applicableCount = getApplicableCartItemCount(cartItems, matchedDiscountCode);
    const isEntirePurchase = matchedDiscountCode?.applicationLevel === 'ENTIRE_PURCHASE';
    const isValidDiscount = isDiscountCodeApplicable(cartItems, matchedDiscountCode);

    const removeButton = (className: string) => readOnly ? null : (
        <button
            type="button"
            onClick={handleRemove}
            className={`shrink-0 text-xs font-light underline underline-offset-2 transition-colors ${className}`}
        >
            {language === Language.GERMAN ? 'entfernen' : 'remove'}
        </button>
    );

    if (isApplied && !isValidating && isValidDiscount) {
        return (
            <div className='flex flex-col gap-1 px-4 py-2.5 rounded-xl border border-green-600/40 bg-green-50'>
                <div className='flex flex-row items-center gap-3'>
                    <Check className='w-4 h-4 text-green-700 shrink-0' />
                    <span className='flex-1 min-w-0 text-sm font-light text-green-700'>
                        <span className='font-semibold'>{discountCode}</span>
                        {' '}{language === Language.GERMAN ? 'wurde angewandt' : 'has been applied'}
                    </span>
                    {removeButton('text-green-700 hover:text-green-800')}
                </div>
                <span className='pl-7 text-xs font-light text-green-700/70'>
                    {formatDiscountLabel()}
                    {' '}&middot;{' '}
                    {isEntirePurchase
                        ? (language === Language.GERMAN ? 'gültig für die gesamte Buchung' : 'applies to entire purchase')
                        : (language === Language.GERMAN
                            ? `gültig für ${applicableCount} ${applicableCount === 1 ? 'Leistung' : 'Leistungen'}`
                            : `applies to ${applicableCount} ${applicableCount === 1 ? 'service' : 'services'}`)
                    }
                </span>
            </div>
        );
    }

    if (isApplied) {
        const errorMessage = isValidating
            ? null
            : getDiscountCodeErrorMessage({
                matchedDiscountCode,
                unmatchedReason,
                applicableCount,
                isEntirePurchase,
                language,
            });

        return (
            <div className='flex flex-col gap-1 px-4 py-2.5 rounded-xl border border-burgundy/30 bg-white'>
                <div className='flex flex-row items-center gap-3'>
                    <Tag className='w-4 h-4 text-burgundy shrink-0' />
                    <span className='flex-1 min-w-0 text-sm font-semibold text-burgundy'>
                        {discountCode}
                        {isValidating && <span className='font-light text-burgundy/60'> ...</span>}
                    </span>
                    {removeButton('text-burgundy hover:text-burgundy/70')}
                </div>
                {errorMessage && (
                    <span className='pl-7 text-xs font-light text-red-600'>
                        {errorMessage}
                    </span>
                )}
            </div>
        );
    }

    if (readOnly) {
        return null;
    }

    return (
        <div className={`flex flex-col rounded-xl border bg-white transition-colors ${isOpen ? 'border-burgundy' : 'border-burgundy/30'}`}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className='flex flex-row items-center gap-3 px-4 py-2.5 text-left'
            >
                <Tag className='w-4 h-4 text-burgundy shrink-0' />
                <span className='flex-1 text-sm font-light text-burgundy'>
                    {language === Language.GERMAN ? (cmsConfig?.discountCodeTitleDe ?? 'Rabattcode') : (cmsConfig?.discountCodeTitleEn ?? 'Discount code')}
                </span>
                {isOpen
                    ? <ChevronDown className='w-4 h-4 text-burgundy/50 shrink-0' />
                    : <ChevronRight className='w-4 h-4 text-burgundy/50 shrink-0' />
                }
            </button>

            {isOpen && (
                <div className='px-4 pb-3'>
                    <div className='relative'>
                        <Input
                            type="text"
                            placeholder={language === Language.GERMAN ? (cmsConfig?.discountCodePlaceholderDe ?? 'Rabattcode eingeben') : (cmsConfig?.discountCodePlaceholderEn ?? 'Enter discount code')}
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && inputValue.trim()) {
                                    handleApply();
                                }
                            }}
                            autoFocus
                            className={`shadow-none h-9 pl-4 pr-24 rounded-full border bg-transparent font-light text-burgundy placeholder:text-burgundy/50 placeholder:text-sm md:placeholder:text-xs focus-visible:ring-0 transition-colors ${inputValue.trim() ? 'border-burgundy' : 'border-burgundy/40 focus-visible:border-burgundy'}`}
                        />
                        <button
                            type="button"
                            onClick={handleApply}
                            disabled={!inputValue.trim()}
                            className='absolute right-4 top-1/2 -translate-y-1/2 text-xs font-semibold uppercase tracking-wide text-burgundy hover:text-burgundy/70 disabled:text-burgundy/40 disabled:cursor-not-allowed transition-colors'
                        >
                            {language === Language.GERMAN ? (cmsConfig?.discountCodeButtonTextDe ?? 'ANWENDEN') : (cmsConfig?.discountCodeButtonTextEn ?? 'APPLY')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DiscountCodeSection;
